import { useMemo } from 'react';
import type { CloudDensity, CloudStyle } from '@/types/weather';
import { CLOUDS } from '../shapePaths';
import { CLOUD_BODIES } from '../shapeBodies';
import { tint, blend } from '../tint';

interface Props {
  cloudDensity: CloudDensity;
  cloudStyle: CloudStyle;
  windMultiplier: number;
  driftDirection: 'left' | 'right';
  skyColor: string;
}

interface CloudInstance {
  id: number;
  top: number;
  scale: number;
  delay: number;
  duration: number;
  opacity: number;
  variant: number;
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed + 3) * 10000;
  return x - Math.floor(x);
}

const TRAVEL = 155;

const CLOUD_COUNT: Record<CloudDensity, number> = {
  none: 0,
  sparse: 2,
  scattered: 4,
  overcast: 6,
};

export function NearCloudsLayer({ cloudDensity, cloudStyle, windMultiplier, driftDirection, skyColor }: Props) {
  const clouds = useMemo<CloudInstance[]>(() => {
    const count = CLOUD_COUNT[cloudDensity];
    return Array.from({ length: count }, (_, i) => {
      const duration = (55 + seededRandom(i * 31) * 30) / windMultiplier;
      const evenX = (i / count) * 100;
      const jitter = (seededRandom(i * 27) - 0.5) * (100 / count) * 0.4;
      const targetX = Math.max(0, Math.min(100, evenX + jitter));
      const delay = driftDirection === 'left'
        ? -(duration * (100 - targetX) / TRAVEL)
        : -(duration * (targetX + 55) / TRAVEL);
      return {
        id: i,
        top: 5 + seededRandom(i * 13) * 53,
        scale: 0.85 + seededRandom(i * 19) * 0.2,
        delay,
        duration,
        opacity: 0.8 + seededRandom(i * 41) * 0.2,
        variant: Math.floor(seededRandom(i * 47) * CLOUDS.length),
      };
    });
  }, [cloudDensity, windMultiplier, driftDirection]);

  const fill = cloudStyle === 'light'
    ? tint(skyColor, 0.8)
    : blend(skyColor, '#4A4D5E', 0.55);
  const animName = driftDirection === 'right' ? 'cloudDriftRight' : 'cloudDriftLeft';

  return (
    <div
      className="layer"
      style={{
        zIndex: 50,
        opacity: cloudDensity === 'none' ? 0 : 1,
        transition: 'opacity 3s ease-in-out',
      }}
    >
      {clouds.map((cloud) => {
        const shape = CLOUDS[cloud.variant] ?? CLOUDS[0]!;
        const body = CLOUD_BODIES[cloud.variant] ?? CLOUD_BODIES[0]!;
        return (
          <div
            key={cloud.id}
            style={{
              position: 'absolute',
              top: `${cloud.top}%`,
              left: 0,
              opacity: cloud.opacity,
              animationName: animName,
              animationDuration: `${cloud.duration}s`,
              animationDelay: `${cloud.delay}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
            }}
          >
            <svg
              viewBox={shape.viewBox}
              aria-hidden="true"
              style={{
                display: 'block',
                width: 'clamp(240px, 26vw, 380px)',
                height: 'auto',
                transform: `scale(${cloud.scale})`,
                transformOrigin: 'left center',
              }}
            >
              <path
                d={body.d}
                fill={fill}
                fillRule={body.fillRule}
                style={{ transition: 'fill 4s ease-in-out' }}
              />
              <path d={shape.d} fill="#0E1252" fillRule={shape.fillRule} />
            </svg>
          </div>
        );
      })}
    </div>
  );
}
